import React, { useState, useCallback } from "react";
import {
  Plus, Trash2, MessageSquare, ArrowLeft, Save, Clock,
  Zap, ChevronDown, ChevronUp, Pencil, Check, X,
  ArrowDown, GitBranch, Smartphone, Users, Phone,
  RefreshCw, AlertCircle,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../lib/apiFetch";

// ─── Types ────────────────────────────────────────────────────────────────────

interface SequenceMessage {
  timing: "immediate" | "delay";
  delayMinutes?: number;
  content: string;
}

interface Sequence {
  id?: string;
  name: string;
  description: string;
  messages: SequenceMessage[];
  triggerOnNewLead: boolean;
  triggerOnStage?: string;
  createdAt: number;
  updatedAt: number;
  ownerId: string;
}

// ─── Flow step definitions ────────────────────────────────────────────────────

interface FlowStep {
  key: string;
  title: string;
  trigger: string;
  defaultText: string;
  vars: string[];
  buttons?: string[];
  color: string;         // tailwind border + bg color
  icon: React.ReactNode;
  delayKey?: string;
  defaultDelayMin?: number;
  note?: string;
}

const FLOW_SECTIONS: { label: string; icon: React.ReactNode; steps: FlowStep[] }[] = [
  {
    label: "Entry Points",
    icon: <Zap className="w-4 h-4" />,
    steps: [
      {
        key: "new_lead_welcome",
        title: "New Lead Detected (Proactive Outreach)",
        trigger: "New lead detected from YogAIn backend (form fill / sign-up) — sent automatically, any time of day",
        defaultText: `Hi {name}! 👋 Welcome to {org}! 🧘‍♀️\n\nYoga, but smarter. Here's what makes us different:\n\n🤖 *AI Posture Tracking* — your phone's camera watches your form in real-time, corrects it on the spot & prevents injuries before they happen\n⚡ *Faster results* — precise alignment means every pose works harder for you\n🛡️ *Zero injury risk* — no guessing, no straining in the wrong direction\n👩‍🏫 *Certified expert instructors* — live guidance, not pre-recorded videos\n🕐 *7 class slots daily* — 6:30 AM to 7 PM, fit any schedule\n📱 *Practice from anywhere* — phone, tablet, or TV\n\nTry it FREE for {trial} days — no credit card needed! 🎁\n\nHow can we help you get started?`,
        vars: ["{name}", "{org}", "{trial}"],
        buttons: ["🎯 Start Free Trial", "📅 Class Schedule", "👩‍🏫 Meet Instructors"],
        color: "border-orange-400 bg-orange-50",
        icon: <Zap className="w-4 h-4 text-orange-600" />,
      },
      {
        key: "greeting_reply",
        title: "Greeting Reply (Inbound)",
        trigger: 'User sends "Hi", "Hello", "Hey", "Namaste", etc. — if flow already started, resends menu',
        defaultText: `Hi {name}! 👋 How can I help you today?\n\nChoose an option below or just type your question 😊`,
        vars: ["{name}"],
        buttons: ["🎯 Start Free Trial", "💰 View Pricing", "💬 Talk to Team"],
        color: "border-green-400 bg-green-50",
        icon: <MessageSquare className="w-4 h-4 text-green-600" />,
      },
    ],
  },
  {
    label: "Trial Start Flow",
    icon: <Smartphone className="w-4 h-4" />,
    steps: [
      {
        key: "device_question",
        title: "Device Question",
        trigger: 'User presses "Start Free Trial"',
        defaultText: `Awesome, {name}! 🎉 Let's get you started with your FREE {trial}-day trial!\n\nWhich device do you use?`,
        vars: ["{name}", "{trial}"],
        buttons: ["🍎 iPhone / iPad", "🤖 Android"],
        color: "border-violet-400 bg-violet-50",
        icon: <Smartphone className="w-4 h-4 text-violet-600" />,
      },
      {
        key: "ios_link",
        title: "iOS Download Link",
        trigger: "User selects iPhone / iPad",
        defaultText: `Here's your {org} download link for iPhone / iPad! 🍎\n\n👇 App Store:\n{link}\n\nDownload it, create your account, and enjoy your FREE {trial}-day trial — no credit card needed!\n\nLet me know once you're in 🙌`,
        vars: ["{org}", "{link}", "{trial}"],
        buttons: ["💬 Contact Team", "📞 Request a Callback"],
        color: "border-slate-400 bg-slate-50",
        icon: <Smartphone className="w-4 h-4 text-slate-600" />,
        note: "{link} is auto-filled from AI Knowledge Base → iOS App Link",
      },
      {
        key: "android_link",
        title: "Android Download Link",
        trigger: "User selects Android",
        defaultText: `Here's your {org} download link for Android! 🤖\n\n👇 Play Store:\n{link}\n\nDownload it, create your account, and enjoy your FREE {trial}-day trial — no credit card needed!\n\nLet me know once you're in 🙌`,
        vars: ["{org}", "{link}", "{trial}"],
        buttons: ["💬 Contact Team", "📞 Request a Callback"],
        color: "border-slate-400 bg-slate-50",
        icon: <Smartphone className="w-4 h-4 text-slate-600" />,
        note: "{link} is auto-filled from AI Knowledge Base → Android App Link",
      },
      {
        key: "qual_q1",
        title: "Welcome + Yoga Background",
        trigger: "User replies after download link OR app download detected from YogAIn backend (+1 in classes)",
        defaultText: `Welcome to the family, {name}❣️! 🧘‍♀️✨\n\nQuick question to personalise your experience — what's your yoga background?`,
        vars: ["{name}"],
        buttons: ["🌱 Complete Beginner", "🌿 Some Experience", "🌳 Regular Practitioner"],
        color: "border-emerald-400 bg-emerald-50",
        icon: <MessageSquare className="w-4 h-4 text-emerald-600" />,
      },
    ],
  },
  {
    label: "Qualification",
    icon: <Users className="w-4 h-4" />,
    steps: [
      {
        key: "qual_q2",
        title: "Wellness Goals Question (Q2)",
        trigger: "User answers yoga background question",
        defaultText: `{ack}\n\nOne more thing — what are your main wellness goals?`,
        vars: ["{ack}"],
        buttons: ["💪 Fitness & Weight Loss", "🧘 Stress Relief", "🌟 General Wellness"],
        color: "border-emerald-400 bg-emerald-50",
        icon: <MessageSquare className="w-4 h-4 text-emerald-600" />,
        note: '{ack} is auto-generated based on Q1 answer (e.g. "Perfect — we have beginner-friendly classes just for you! 🌱")',
      },
      {
        key: "qual_complete",
        title: "Qualification Complete",
        trigger: "User answers wellness goals question → lead moves to Qualified",
        defaultText: `{goal_msg}\n\nYou're all set, {name}! 🎉 Start exploring classes on the app. We're here if you need anything!\n\nEnjoy your journey! 🧘‍♀️✨`,
        vars: ["{goal_msg}", "{name}"],
        color: "border-emerald-400 bg-emerald-50",
        icon: <Check className="w-4 h-4 text-emerald-600" />,
        note: '{goal_msg} is auto-generated based on goal answer (e.g. "We\'ll get you moving and burning! 💪🔥")',
      },
    ],
  },
  {
    label: "Class Feedback",
    icon: <MessageSquare className="w-4 h-4" />,
    steps: [
      {
        key: "feedback_request",
        title: "Feedback Request",
        trigger: "Class attended detected from YogAIn backend (yogain_total_classes increases)",
        defaultText: `Hey {name}! 👋 How was your first class with Yogain? We'd love to hear your thoughts! 🧘‍♀️`,
        vars: ["{name}"],
        buttons: ["🔥 Loved it!", "👍 Pretty good", "😐 Could be better"],
        color: "border-amber-400 bg-amber-50",
        icon: <MessageSquare className="w-4 h-4 text-amber-600" />,
      },
      {
        key: "feedback_positive",
        title: "Positive Feedback Response",
        trigger: 'User selects "Loved it!" or "Pretty good" → lead moves to Hot + Exotel call triggered',
        defaultText: `That's amazing, {name}! 🎉🔥 So glad you're loving it!\n\nHere's how to continue after your trial:\n\n{pricing}`,
        vars: ["{name}", "{pricing}"],
        color: "border-green-400 bg-green-50",
        icon: <Check className="w-4 h-4 text-green-600" />,
        note: "{pricing} is auto-filled from AI Knowledge Base → Pricing Plans",
      },
      {
        key: "feedback_negative",
        title: "Negative Feedback Response",
        trigger: 'User selects "Could be better" → lead moves to Human Intervention',
        defaultText: `Thank you for your honest feedback, {name}! 🙏\n\nWe're sorry it wasn't perfect. Our team will reach out shortly to understand how we can improve your experience! 💪`,
        vars: ["{name}"],
        color: "border-red-300 bg-red-50",
        icon: <AlertCircle className="w-4 h-4 text-red-500" />,
      },
    ],
  },
  {
    label: "Menu Options",
    icon: <GitBranch className="w-4 h-4" />,
    steps: [
      {
        key: "pricing",
        title: "Pricing Info",
        trigger: 'User presses "View Pricing"',
        defaultText: `💰 Our pricing:\n\n{pricing}\n\nBut first — try FREE for {trial} days, no credit card needed! 🎁`,
        vars: ["{pricing}", "{trial}"],
        buttons: ["🎯 Start Free Trial", "💬 Talk to Team"],
        color: "border-orange-400 bg-orange-50",
        icon: <MessageSquare className="w-4 h-4 text-orange-600" />,
        note: "{pricing} is auto-filled from AI Knowledge Base → Pricing Plans",
      },
      {
        key: "class_timings",
        title: "Class Schedule",
        trigger: 'User presses "Class Schedule"',
        defaultText: `📅 Our class schedule:\n\n{timings}\n\nJoin any class during your FREE {trial}-day trial! 🎯`,
        vars: ["{timings}", "{trial}"],
        color: "border-cyan-400 bg-cyan-50",
        icon: <Clock className="w-4 h-4 text-cyan-600" />,
        note: "{timings} is auto-filled from AI Knowledge Base → Class Timings",
      },
      {
        key: "meet_instructors",
        title: "Meet Instructors",
        trigger: 'User presses "Meet Instructors"',
        defaultText: `👩‍🏫 Meet our instructors:\n\n{instructors}\n\nAll classes are led by these experts — try FREE for {trial} days! 🧘‍♀️`,
        vars: ["{instructors}", "{trial}"],
        color: "border-pink-400 bg-pink-50",
        icon: <Users className="w-4 h-4 text-pink-600" />,
        note: "{instructors} is auto-filled from AI Knowledge Base → Instructor Profiles",
      },
      {
        key: "talk_to_team",
        title: "Talk to Team",
        trigger: 'User presses "Talk to Team" → lead moves to Human Intervention',
        defaultText: `No worries {name}! 😊 Our team will reach out to you shortly.\n\nA {org} specialist will help you with everything! 🙌`,
        vars: ["{name}", "{org}"],
        color: "border-red-300 bg-red-50",
        icon: <Users className="w-4 h-4 text-red-500" />,
      },
    ],
  },
  {
    label: "Auto Follow-ups (Scheduler)",
    icon: <Clock className="w-4 h-4" />,
    steps: [
      {
        key: "followup_1",
        title: "Follow-up 1",
        trigger: "Sent automatically after delay if lead hasn't responded",
        defaultText: `Hey {name}! 🌟 Just a gentle nudge — your free Yogain trial is still waiting for you!\n\nThousands are transforming their wellness with just 45 min of yoga a day. You're one tap away 🧘‍♀️💪\n\nLet me know if you have any questions — I'm here to help! 😊`,
        vars: ["{name}"],
        color: "border-indigo-400 bg-indigo-50",
        icon: <Clock className="w-4 h-4 text-indigo-600" />,
        delayKey: "delay_followup1_min",
        defaultDelayMin: 5,
      },
      {
        key: "followup_2",
        title: "Follow-up 2",
        trigger: "Sent automatically after delay if no response to Follow-up 1",
        defaultText: `Hi {name} 😊 Life gets busy — we totally get it!\n\nBut here's the thing: even one yoga class can shift how you feel today. Calmer mind, stronger body, better sleep 🌙✨\n\nYour free trial is still open. Want me to help you pick the perfect class to start with? 🎯`,
        vars: ["{name}"],
        color: "border-indigo-400 bg-indigo-50",
        icon: <Clock className="w-4 h-4 text-indigo-600" />,
        delayKey: "delay_followup2_min",
        defaultDelayMin: 30,
      },
    ],
  },
];

// ─── FlowNode component ────────────────────────────────────────────────────────

function FlowNode({
  step,
  value,
  delayValue,
  onSave,
}: {
  step: FlowStep;
  value: string;
  delayValue?: string;
  onSave: (key: string, text: string, delayKey?: string, delay?: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [draftDelay, setDraftDelay] = useState(delayValue ?? String(step.defaultDelayMin ?? ""));
  const displayText = value || step.defaultText;

  const handleEdit = () => { setDraft(value || step.defaultText); setDraftDelay(delayValue ?? String(step.defaultDelayMin ?? "")); setEditing(true); };
  const handleCancel = () => setEditing(false);
  const handleSave = () => { onSave(step.key, draft, step.delayKey, step.delayKey ? draftDelay : undefined); setEditing(false); };
  const isCustom = !!value && value !== step.defaultText;

  return (
    <div className={`rounded-xl border-l-4 ${step.color} border border-opacity-60 shadow-sm overflow-hidden`}>
      {/* Header */}
      <div className="flex items-start justify-between px-4 py-3">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <div className="mt-0.5 shrink-0">{step.icon}</div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm text-slate-800">{step.title}</span>
              {isCustom && (
                <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">Customised</span>
              )}
            </div>
            <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{step.trigger}</p>
          </div>
        </div>
        {!editing && (
          <button onClick={handleEdit} className="p-1.5 rounded-lg hover:bg-white/60 text-slate-400 hover:text-slate-700 transition-colors shrink-0 ml-2">
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Delay input (for follow-ups) */}
      {step.delayKey && (
        <div className="px-4 pb-2 flex items-center gap-2">
          <Clock className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
          {editing ? (
            <div className="flex items-center gap-1.5 text-xs text-slate-600">
              <span>Send after</span>
              <input
                type="number"
                min={1}
                value={draftDelay}
                onChange={(e) => setDraftDelay(e.target.value)}
                className="w-16 border border-slate-300 rounded px-2 py-0.5 text-center text-xs focus:outline-none focus:border-indigo-500"
              />
              <span>minutes of no response</span>
            </div>
          ) : (
            <span className="text-xs text-slate-500">
              Sends <strong>{delayValue || step.defaultDelayMin} min</strong> after last outbound (if no reply)
            </span>
          )}
        </div>
      )}

      {/* Message preview / editor */}
      <div className="px-4 pb-3">
        {editing ? (
          <div className="space-y-2">
            <textarea
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 bg-white"
              rows={5}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Message text..."
            />
            <div className="flex flex-wrap gap-1 items-center">
              <span className="text-[10px] text-slate-400 mr-1">Variables:</span>
              {step.vars.map((v) => (
                <button
                  key={v}
                  onClick={() => setDraft((d) => d + v)}
                  className="text-[10px] bg-white border border-slate-200 text-slate-500 px-1.5 py-0.5 rounded hover:bg-slate-50 font-mono"
                >{v}</button>
              ))}
            </div>
            {step.note && (
              <p className="text-[10px] text-amber-600 bg-amber-50 rounded px-2 py-1">{step.note}</p>
            )}
            <div className="flex gap-2">
              <button onClick={handleSave} className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 font-medium">
                <Check className="w-3 h-3" /> Save
              </button>
              <button onClick={handleCancel} className="flex items-center gap-1 px-3 py-1.5 bg-white border text-slate-600 text-xs rounded-lg hover:bg-slate-50">
                <X className="w-3 h-3" /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white/70 rounded-lg px-3 py-2 border border-white">
            <p className="text-xs text-slate-700 whitespace-pre-wrap line-clamp-4">{displayText}</p>
            {step.note && (
              <p className="text-[10px] text-amber-600 mt-1">{step.note}</p>
            )}
          </div>
        )}
      </div>

      {/* CTA buttons (display only) */}
      {step.buttons && !editing && (
        <div className="px-4 pb-3 flex flex-wrap gap-1">
          {step.buttons.map((b) => (
            <span key={b} className="text-[10px] bg-white border border-slate-200 text-slate-500 px-2 py-0.5 rounded-full">{b}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Sequences editor (fixed to use SQLite API) ───────────────────────────────

function SequencesTab() {
  const [editingSequence, setEditingSequence] = useState<Sequence | null>(null);
  const queryClient = useQueryClient();

  const { data: sequences = [], isLoading } = useQuery<Sequence[]>({
    queryKey: ["sequences"],
    queryFn: async () => {
      const res = await apiFetch("/api/sequences");
      if (!res.ok) return [];
      return res.json();
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (seq: Sequence) => {
      if (seq.id) {
        await apiFetch(`/api/sequences/${seq.id}`, { method: "PUT", body: JSON.stringify(seq) });
      } else {
        await apiFetch("/api/sequences", { method: "POST", body: JSON.stringify(seq) });
      }
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["sequences"] }); setEditingSequence(null); },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => { await apiFetch(`/api/sequences/${id}`, { method: "DELETE" }); },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["sequences"] }),
  });

  if (editingSequence) {
    return (
      <div className="max-w-3xl mx-auto w-full p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setEditingSequence(null)} className="p-2 rounded-lg hover:bg-slate-200 transition-colors">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <h2 className="text-xl font-bold text-slate-900">{editingSequence.id ? "Edit Sequence" : "New Sequence"}</h2>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setEditingSequence(null)} className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50">Cancel</button>
            <button onClick={() => saveMutation.mutate(editingSequence)} disabled={saveMutation.isPending}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 flex items-center gap-2">
              <Save className="w-4 h-4" />{saveMutation.isPending ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
        <div className="bg-white p-5 rounded-xl border space-y-4">
          <input className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-green-500"
            placeholder="Sequence name" value={editingSequence.name}
            onChange={(e) => setEditingSequence({ ...editingSequence, name: e.target.value })} />
          <input className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-green-500"
            placeholder="Description" value={editingSequence.description}
            onChange={(e) => setEditingSequence({ ...editingSequence, description: e.target.value })} />
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" checked={editingSequence.triggerOnNewLead}
              onChange={(e) => setEditingSequence({ ...editingSequence, triggerOnNewLead: e.target.checked })}
              className="text-green-600 rounded" />
            Auto-trigger on new leads
          </label>
        </div>
        <div className="space-y-4">
          <h3 className="text-base font-semibold text-slate-800">Messages Timeline</h3>
          {(editingSequence.messages || []).map((msg, idx) => (
            <div key={idx} className="bg-white p-5 rounded-xl border relative">
              <button className="absolute top-4 right-4 text-slate-400 hover:text-red-500 p-1"
                onClick={() => setEditingSequence({ ...editingSequence, messages: editingSequence.messages.filter((_, i) => i !== idx) })}>
                <Trash2 className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-xs font-bold">{idx + 1}</div>
                <span className="font-medium text-sm text-slate-800">Message {idx + 1}</span>
              </div>
              <div className="space-y-3">
                <div className="flex gap-4">
                  {(["immediate", "delay"] as const).map((t) => (
                    <label key={t} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                      <input type="radio" checked={msg.timing === t}
                        onChange={() => setEditingSequence({ ...editingSequence, messages: editingSequence.messages.map((m, i) => i === idx ? { ...m, timing: t } : m) })}
                        className="text-green-600" />
                      {t === "immediate" ? <><Zap className="w-3.5 h-3.5 text-slate-400" /> Immediate</> : <><Clock className="w-3.5 h-3.5 text-slate-400" /> After delay</>}
                    </label>
                  ))}
                </div>
                {msg.timing === "delay" && (
                  <div className="flex items-center gap-2">
                    <input type="number" min={1} value={msg.delayMinutes || 60}
                      onChange={(e) => setEditingSequence({ ...editingSequence, messages: editingSequence.messages.map((m, i) => i === idx ? { ...m, delayMinutes: Number(e.target.value) } : m) })}
                      className="w-24 border border-slate-200 rounded px-3 py-1.5 text-sm outline-none focus:border-green-500" />
                    <span className="text-sm text-slate-500">minutes delay</span>
                  </div>
                )}
                <textarea className="w-full h-28 border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none outline-none focus:border-green-500"
                  value={msg.content}
                  onChange={(e) => setEditingSequence({ ...editingSequence, messages: editingSequence.messages.map((m, i) => i === idx ? { ...m, content: e.target.value } : m) })}
                  placeholder="Message content..." />
                <div className="flex gap-2 flex-wrap">
                  <span className="text-xs text-slate-400">Variables:</span>
                  {["{{+lead_name}}", "{{+user_name}}", "{{+org_name}}"].map((tag) => (
                    <button key={tag} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded hover:bg-slate-200"
                      onClick={() => setEditingSequence({ ...editingSequence, messages: editingSequence.messages.map((m, i) => i === idx ? { ...m, content: m.content + " " + tag } : m) })}>
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
          <button onClick={() => setEditingSequence({ ...editingSequence, messages: [...(editingSequence.messages || []), { timing: "immediate", content: "" }] })}
            className="w-full py-3 border-2 border-dashed border-green-300 text-green-700 text-sm font-medium rounded-xl hover:bg-green-50 flex items-center justify-center gap-2">
            <Plus className="w-4 h-4" /> Add Message Step
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto w-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Drip Sequences</h2>
          <p className="text-sm text-slate-500 mt-1">Automated drip campaigns triggered by lead behavior.</p>
        </div>
        <button onClick={() => setEditingSequence({ name: "New Sequence", description: "", messages: [], triggerOnNewLead: false, createdAt: 0, updatedAt: 0, ownerId: "" })}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
          <Plus className="w-4 h-4" /> New Sequence
        </button>
      </div>
      {isLoading ? (
        <div className="text-center p-12 text-slate-500 bg-white rounded-xl border">Loading…</div>
      ) : sequences.length === 0 ? (
        <div className="text-center p-12 text-slate-500 bg-white rounded-xl border">
          <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-base font-medium mb-1">No sequences yet</p>
          <p className="text-sm">Create one to start automated drip messaging.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sequences.map((seq) => (
            <div key={seq.id} className="bg-white border rounded-xl p-5 hover:shadow-md transition-all cursor-pointer group relative" onClick={() => setEditingSequence(seq)}>
              <button onClick={(e) => { e.stopPropagation(); if (seq.id) deleteMutation.mutate(seq.id); }}
                className="absolute top-4 right-4 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-1">
                <Trash2 className="w-4 h-4" />
              </button>
              <h3 className="text-base font-semibold text-slate-900 group-hover:text-green-600 transition-colors pr-8">{seq.name}</h3>
              <p className="text-sm text-slate-500 mt-1 mb-4 line-clamp-2">{seq.description || "No description"}</p>
              <div className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 px-3 py-1.5 rounded-full w-max">
                <MessageSquare className="w-4 h-4 text-slate-400" />{seq.messages?.length || 0} messages
              </div>
              {seq.triggerOnNewLead && (
                <span className="mt-2 inline-block text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Auto-trigger on new leads</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

export function AutoFollowUp() {
  const [activeTab, setActiveTab] = useState<"flow" | "sequences">("flow");
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  const { data: flowMsgs = {}, refetch } = useQuery<Record<string, string>>({
    queryKey: ["flow-messages"],
    queryFn: async () => {
      const res = await apiFetch("/api/flow-messages");
      if (!res.ok) return {};
      return res.json();
    },
  });

  const handleSaveStep = useCallback(async (key: string, text: string, delayKey?: string, delay?: string) => {
    setSaving(true);
    setSaveMsg("");
    try {
      const payload: Record<string, string> = { [key]: text };
      if (delayKey && delay) payload[delayKey] = delay;
      const res = await apiFetch("/api/flow-messages", { method: "POST", body: JSON.stringify(payload) });
      if (res.ok) { setSaveMsg("Saved!"); refetch(); }
      else setSaveMsg("Save failed");
    } catch { setSaveMsg("Save failed"); }
    finally { setSaving(false); setTimeout(() => setSaveMsg(""), 2000); }
  }, [refetch]);

  const toggleSection = (label: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      next.has(label) ? next.delete(label) : next.add(label);
      return next;
    });
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 overflow-hidden">
      {/* Tab bar */}
      <div className="flex border-b bg-white px-6 shrink-0 items-center justify-between">
        <div className="flex">
          <button onClick={() => setActiveTab("flow")}
            className={`px-5 py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === "flow" ? "border-green-600 text-green-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            Conversation Flow
          </button>
          <button onClick={() => setActiveTab("sequences")}
            className={`px-5 py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === "sequences" ? "border-green-600 text-green-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            Drip Sequences
          </button>
        </div>
        {activeTab === "flow" && (
          <div className="flex items-center gap-3">
            {saveMsg && <span className={`text-xs font-medium ${saveMsg === "Saved!" ? "text-green-600" : "text-red-500"}`}>{saveMsg}</span>}
            {saving && <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" />}
          </div>
        )}
      </div>

      {activeTab === "sequences" ? (
        <div className="flex-1 overflow-y-auto"><SequencesTab /></div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-2xl mx-auto px-4 py-6 space-y-3">
            {/* Header */}
            <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center shrink-0">
                <Zap className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <h1 className="text-sm font-bold text-slate-900">Automated Conversation Flow</h1>
                <p className="text-xs text-slate-500 mt-0.5">Click the ✏️ icon on any step to edit its message. Changes take effect immediately.</p>
              </div>
            </div>

            {/* Trigger node */}
            <div className="flex flex-col items-center gap-1">
              <div className="w-full bg-slate-800 text-white rounded-xl px-4 py-3 flex items-center gap-3">
                <MessageSquare className="w-5 h-5 text-green-400 shrink-0" />
                <div>
                  <p className="text-sm font-semibold">Trigger: New WhatsApp Message Received</p>
                  <p className="text-xs text-slate-400 mt-0.5">All automation starts here. AI Auto-Reply must be ON for the lead.</p>
                </div>
              </div>
              <ArrowDown className="w-4 h-4 text-slate-400" />
            </div>

            {/* Flow sections */}
            {FLOW_SECTIONS.map((section, si) => {
              const collapsed = collapsedSections.has(section.label);
              return (
                <div key={section.label} className="space-y-1">
                  {/* Section header */}
                  <button
                    onClick={() => toggleSection(section.label)}
                    className="w-full flex items-center justify-between px-4 py-2.5 bg-white rounded-xl border hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                      <span className="text-slate-500">{section.icon}</span>
                      {section.label}
                      <span className="text-xs font-normal text-slate-400">({section.steps.length} steps)</span>
                    </div>
                    {collapsed ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronUp className="w-4 h-4 text-slate-400" />}
                  </button>

                  {!collapsed && (
                    <div className="pl-4 space-y-2 border-l-2 border-slate-200 ml-4">
                      {section.steps.map((step, i) => (
                        <React.Fragment key={step.key}>
                          <FlowNode
                            step={step}
                            value={flowMsgs[step.key] || ""}
                            delayValue={step.delayKey ? flowMsgs[step.delayKey] : undefined}
                            onSave={handleSaveStep}
                          />
                          {i < section.steps.length - 1 && (
                            <div className="flex justify-start pl-4">
                              <ArrowDown className="w-3 h-3 text-slate-300" />
                            </div>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  )}

                  {si < FLOW_SECTIONS.length - 1 && (
                    <div className="flex justify-center py-1">
                      <ArrowDown className="w-4 h-4 text-slate-300" />
                    </div>
                  )}
                </div>
              );
            })}

            {/* Auto follow-up legend */}
            <div className="bg-white border rounded-xl p-4 mt-2">
              <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">Flow State Legend</h3>
              <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                {[
                  ["menu_sent", "Main menu sent, waiting for selection"],
                  ["awaiting_device", "Waiting for iOS / Android choice"],
                  ["ios_link_sent / android_link_sent", "Download link sent, waiting for reply or app install"],
                  ["qual_q1_sent", "Yoga background question sent"],
                  ["qual_q2_sent", "Goals question sent"],
                  ["qualified", "Fully qualified, AI continues"],
                  ["feedback_requested", "Feedback message sent"],
                  ["hot", "Positive feedback — hot lead"],
                  ["human_intervention", "Handed to human agent"],
                  ["class_time_asked", "Asking for preferred class time"],
                  ["class_suggested", "Class suggestion sent"],
                ].map(([state, desc]) => (
                  <div key={state} className="flex items-start gap-1.5">
                    <code className="bg-slate-100 text-slate-600 px-1 py-0.5 rounded text-[10px] shrink-0 font-mono">{state}</code>
                    <span className="text-slate-500 text-[10px] leading-snug">{desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
